import { Component, computed, ElementRef, inject, signal, viewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { HeaderComponent } from '../../components/header/header.component';
import { LoadingComponent } from '../../components/loading/loading.component';
import { InputComponent } from '../../components/input/input.component';
import { TextAreaComponent } from '../../components/text-area/text-area.component';
import { ButtonComponent } from '../../components/button/button.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormBuilder, FormGroup, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { PsychologistService } from '../../services/psychologist.service';
import { Psychologist } from '../../models/psychologists';
import { UserPsychologistService } from '../../services/user-psychologist.service';
import { UserPsychologist } from '../../models/user-psychologist';
import { User } from '../../models/user';
import { AppointmentService } from '../../services/appointment.service';
import { Appointment } from '../../models/appointment';
import TYPES from '../../constants/types';
import { AppointmentReportPayload } from '../../types/appointment-report';

@Component({
  selector: 'app-appointments-report',
  imports: [
    HeaderComponent,
    LoadingComponent,
    InputComponent,
    TextAreaComponent,
    ButtonComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    TranslatePipe
  ],
  templateUrl: './appointments-report.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './appointments-report.component.scss'
})
export class AppointmentsReportComponent {
  authService: AuthService = inject(AUTH_SERVICE);
  psychologistService: PsychologistService<Psychologist> = inject(PsychologistService);
  userPsychologistService: UserPsychologistService<UserPsychologist> = inject(UserPsychologistService);
  appointmentService: AppointmentService<Appointment> = inject(AppointmentService);
  translate: TranslateService = inject(TranslateService);
  snackBar: MatSnackBar = inject(MatSnackBar);

  reportForm: FormGroup;
  patientControl: UntypedFormControl;
  startDateControl: UntypedFormControl;
  endDateControl: UntypedFormControl;
  commentsControl: UntypedFormControl;

  patients: WritableSignal<User[]> = signal<User[]>([]);
  patientSearchTerm: WritableSignal<string> = signal('');
  patientSearchInput = viewChild<ElementRef<HTMLInputElement>>('patientSearchInput');

  selectedPsychologistId: WritableSignal<string | null> = signal<string | null>(null);

  loadingPatients: WritableSignal<boolean> = signal<boolean>(false);
  loadingReport: WritableSignal<boolean> = signal<boolean>(false);
  formInvalid: WritableSignal<boolean> = signal<boolean>(true);

  dateRangeError: WritableSignal<'start_after_end' | 'max_range' | null> = signal<'start_after_end' | 'max_range' | null>(null);

  BUTTON_TYPES: any;
  PROFILES: any;

  filteredPatients = computed(() => {
    const term = this.patientSearchTerm().trim().toLowerCase();

    if(!term){
      return this.patients();
    }

    return this.patients().filter((patient) => {
      const fullName = `${patient.first_name ?? ''} ${patient.last_name ?? ''}`.toLowerCase();
      return fullName.includes(term);
    });
  });

  isProcessing = computed(() => this.loadingPatients() || this.loadingReport());
  hasPatients = computed(() => this.patients().length > 0);
  isGenerateDisabled = computed(() => {
    return this.formInvalid() || !!this.dateRangeError() || this.loadingReport() || !this.hasPatients();
  });

  constructor(
    private formBuilder: FormBuilder,
    private router: Router
  ) {
    this.BUTTON_TYPES = TYPES.UI.BUTTON;
    this.PROFILES = TYPES.DATABASE.PROFILES;

    this.patientControl = new UntypedFormControl('', [Validators.required]);
    this.startDateControl = new UntypedFormControl('', [Validators.required]);
    this.endDateControl = new UntypedFormControl('', [Validators.required]);
    this.commentsControl = new UntypedFormControl('');

    this.reportForm = this.formBuilder.group({
      patient: this.patientControl,
      startDate: this.startDateControl,
      endDate: this.endDateControl,
      comments: this.commentsControl
    });
    this.formInvalid.set(this.reportForm.invalid);
    this.reportForm.statusChanges.subscribe(() => {
      this.formInvalid.set(this.reportForm.invalid);
    });

    this.startDateControl.valueChanges.subscribe(() => this.validateDateRange());
    this.endDateControl.valueChanges.subscribe(() => this.validateDateRange());

    this.loadPatients();
  }

  async loadPatients(): Promise<void> {
    this.loadingPatients.set(true);

    try{
      if(!this.authService.user()){
        await this.authService.getUser();
      }

      if(this.authService.user()?.profile !== this.PROFILES.PSYCHOLOGIST.key){
        this.router.navigate(['/home'], { replaceUrl: true });
        return;
      }

      let psychologistId = this.psychologistService.psychologist()?.id ?? null;
      if(!psychologistId){
        const userId = this.authService.user()?.id;
        if(userId){
          const psychologist = await this.psychologistService.getPsychologistByUserId(userId);
          psychologistId = psychologist?.id ?? null;
        }
      }

      this.selectedPsychologistId.set(psychologistId);

      if(!psychologistId){
        this.patients.set([]);
        return;
      }

      const usersPsychologists = await this.userPsychologistService.getAllActiveUsersPsychologistsByPsychologistId(psychologistId);
      const activePatients = usersPsychologists
        .map((item) => item.users)
        .filter((item): item is User => !!item)
        .sort((a, b) => `${a.first_name ?? ''} ${a.last_name ?? ''}`.localeCompare(`${b.first_name ?? ''} ${b.last_name ?? ''}`));

      this.patients.set(activePatients);
    }catch(error: any){
      const message = this.translate.instant('info.unkonwn_error', { error: error?.toString() ?? String(error) });
      const action = this.translate.instant('buttons.dismiss');
      this.snackBar.open(message, action);
      this.patients.set([]);
    }finally{
      this.loadingPatients.set(false);
    }
  }

  onPatientSelectOpened(opened: boolean): void {
    if(opened){
      setTimeout(() => this.patientSearchInput()?.nativeElement.focus());
      return;
    }

    this.patientSearchTerm.set('');
  }

  onPatientSearch(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.patientSearchTerm.set(target?.value ?? '');
  }

  back(): void {
    history.back();
  }

  validateDateRange(): boolean {
    if(!this.startDateControl.value || !this.endDateControl.value){
      this.dateRangeError.set(null);
      return true;
    }

    const startDate = this.getStartOfDay(this.startDateControl.value);
    const endDate = this.getEndOfDay(this.endDateControl.value);

    if(startDate.getTime() > endDate.getTime()){
      this.dateRangeError.set('start_after_end');
      return false;
    }

    const dayDiff = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    if(dayDiff > 365){
      this.dateRangeError.set('max_range');
      return false;
    }

    this.dateRangeError.set(null);
    return true;
  }

  getDateRangeErrorKey(): string {
    if(this.dateRangeError() === 'start_after_end'){
      return 'appointments-report.errors.start_after_end';
    }

    if(this.dateRangeError() === 'max_range'){
      return 'appointments-report.errors.max_range';
    }

    return '';
  }

  async generateReport(): Promise<void> {
    if(this.isGenerateDisabled() || !this.validateDateRange()){
      this.reportForm.markAllAsTouched();
      this.reportForm.updateValueAndValidity();
      return;
    }

    const psychologistId = this.selectedPsychologistId();
    const patientId = this.patientControl.value as string | null;

    if(!psychologistId || !patientId){
      return;
    }

    this.loadingReport.set(true);

    try{
      const selectedPatient = this.patients().find((patient) => patient.id === patientId) ?? null;
      const startDate = this.getStartOfDay(this.startDateControl.value);
      const endDate = this.getEndOfDay(this.endDateControl.value);
      const comments = (this.commentsControl.value ?? '').toString().trim();

      const appointments = await this.appointmentService.getConfirmedAppointmentsByPsychologistPatientAndDateRange(
        psychologistId,
        patientId,
        startDate.toISOString(),
        endDate.toISOString()
      );

      const reportLines = appointments.map((item) => ({
        appointmentDate: item.date ?? '',
        currency: item.currency ?? '',
        value: item.value ?? 0
      }));

      const totalsMap = new Map<string, number>();
      for(const line of reportLines){
        const current = totalsMap.get(line.currency) ?? 0;
        totalsMap.set(line.currency, current + line.value);
      }

      const totals = Array.from(totalsMap.entries()).map(([currency, total]) => ({
        currency,
        total
      }));

      const reportPayload: AppointmentReportPayload = {
        lines: reportLines,
        totals,
        meta: {
          reportName: this.translate.instant('appointments-report.title'),
          patientName: selectedPatient ? `${selectedPatient.first_name ?? ''} ${selectedPatient.last_name ?? ''}`.trim() : '',
          psychologistName: `${this.authService.user()?.first_name ?? ''} ${this.authService.user()?.last_name ?? ''}`.trim(),
          psychologistEmail: this.authService.user()?.email ?? '',
          psychologistLicenseNumber: this.psychologistService.psychologist()?.council_number ?? '',
          psychologistLicenseCountry: this.psychologistService.psychologist()?.council_country ?? '',
          comments,
          startDateIso: startDate.toISOString(),
          endDateIso: endDate.toISOString(),
          generatedAtIso: new Date().toISOString()
        }
      };

      this.router.navigate(['/appointments-report-view'], {
        state: {
          report: reportPayload
        }
      });
    }catch(error: any){
      const message = this.translate.instant('info.unkonwn_error', { error: error?.toString() ?? String(error) });
      const action = this.translate.instant('buttons.dismiss');
      this.snackBar.open(message, action);
    }finally{
      this.loadingReport.set(false);
    }
  }

  private getStartOfDay(dateInput: Date): Date {
    const date = new Date(dateInput);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private getEndOfDay(dateInput: Date): Date {
    const date = new Date(dateInput);
    date.setHours(23, 59, 59, 999);
    return date;
  }
}
